import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { importLibrary } from "@googlemaps/js-api-loader";
import { configureGoogleMaps } from "@/utils/googleMapsLoader.ts";
import { InputProps } from "react-daisyui";
import { cn } from "@/utils/classNames.ts";
import assignChangedProperties from "@/utils/assignChangedProperties.ts";
import addSuggestionRoom from "@/utils/addSuggestionRoom.ts";

type AutocompleteInputProps =
  & Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "size" | "color"
  >
  & Pick<InputProps, "size" | "color" | "bordered">
  & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    includedPrimaryTypes?: string[];
    includeDisplayName?: boolean;
    restrictToBiasLocation?: boolean;
    biasLocation?: string | null;
    preserveSelectionText?: boolean;
    onPlaceSelect?: (selection: { placeId: string; text: string } | null) => void;
    onReadyChange?: (ready: boolean) => void;
  };

type WidgetProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  label?: string;
  maxLength?: number;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  value: string;
  includedPrimaryTypes?: string[];
};

type ResolvedLocation = {
  location: google.maps.LatLng;
  viewport: google.maps.LatLngBoundsLiteral;
};

const locationBiasCache = new Map<
  string,
  Promise<ResolvedLocation | null>
>();

const resolveLocationBias = (
  destination: string,
): Promise<ResolvedLocation | null> => {
  const normalizedDestination = destination.trim();
  const cacheKey = normalizedDestination.toLocaleLowerCase();
  const cachedLocation = locationBiasCache.get(cacheKey);

  if (cachedLocation) {
    return cachedLocation;
  }

  const locationRequest = (async () => {
    const { Place } = await importLibrary("places");
    const { places } = await Place.searchByText({
      textQuery: normalizedDestination,
      fields: ["location", "viewport"],
      maxResultCount: 1,
    });

    const result = places[0];

    if (!result?.location || !result.viewport) {
      return null;
    }

    const northEast = result.viewport.getNorthEast();
    const southWest = result.viewport.getSouthWest();

    return {
      location: result.location,
      viewport: {
        north: northEast.lat(),
        east: northEast.lng(),
        south: southWest.lat(),
        west: southWest.lng(),
      },
    };
  })();

  locationBiasCache.set(cacheKey, locationRequest);

  locationRequest.catch(() => {
    if (locationBiasCache.get(cacheKey) === locationRequest) {
      locationBiasCache.delete(cacheKey);
    }
  });

  return locationRequest;
};

const getInputValue = (
  value: InputHTMLAttributes<HTMLInputElement>["value"],
): string => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return value === undefined || value === null ? "" : String(value);
};

const formatSelection = (
  displayName: string | undefined,
  formattedAddress: string | undefined,
  includeDisplayName: boolean,
): string | undefined => {
  if (!includeDisplayName || !displayName) {
    return formattedAddress ?? displayName;
  }

  if (!formattedAddress) {
    return displayName;
  }

  return formattedAddress.startsWith(displayName)
    ? formattedAddress
    : `${displayName}, ${formattedAddress}`;
};

const SUGGESTION_ROOM_PX = 320;
const SUGGESTION_ROOM_RELEASE_DELAY_MS = 300;

const findScrollParent = (element: HTMLElement): HTMLElement => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }

  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
};

const visibleBottom = (): number =>
  window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : window.innerHeight;

const suggestionShortfall = (element: Element): number =>
  Math.ceil(SUGGESTION_ROOM_PX - (visibleBottom() - element.getBoundingClientRect().bottom));

const applyWidgetProps = (
  element: google.maps.places.PlaceAutocompleteElement,
  props: WidgetProps,
) => {
  assignChangedProperties(element, {
    value: props.value,
    placeholder: props.placeholder ?? "",
    description: props.label ?? props.placeholder ?? "Destination",
    disabled: Boolean(props.disabled),
    readonly: Boolean(props.readOnly),
    name: props.name ?? "",
    noInputIcon: true,
    ...(props.includedPrimaryTypes?.length ? { includedPrimaryTypes: props.includedPrimaryTypes } : {}),
  });

  if (props.id) {
    element.id = props.id;
  } else {
    element.removeAttribute("id");
  }

  if (typeof props.maxLength === "number") {
    element.maxlength = props.maxLength;
  } else {
    element.removeAttribute("maxlength");
  }

  if (props.error) {
    element.setAttribute("aria-invalid", "true");
  } else {
    element.removeAttribute("aria-invalid");
  }

  const className = cn(
    "block w-full max-w-full rounded-lg",
    props.error && "border-error",
    props.className,
  );

  if (element.className !== className) {
    element.className = className;
  }

  element.style.width = "100%";
  element.style.maxWidth = "100%";
  element.style.boxSizing = "border-box";
  element.style.borderRadius = "0.5rem";
  element.style.padding = "0";
  element.style.setProperty("color-scheme", "only light", "important");
  element.style.setProperty("background-color", "#ffffff", "important");
  element.style.setProperty("color", "#0a0a0a", "important");
};

const AutocompleteInput = forwardRef<
  HTMLInputElement,
  AutocompleteInputProps
>(
  (
    {
      label,
      error,
      wrapperClassName,
      className,
      size = "md",
      color,
      bordered = true,
      value,
      defaultValue,
      placeholder,
      disabled,
      readOnly,
      name,
      id,
      maxLength,
      includedPrimaryTypes = ["(cities)"],
      includeDisplayName = false,
      biasLocation,
      restrictToBiasLocation = false,
      preserveSelectionText = false,
      onPlaceSelect,
      onReadyChange,
      onChange,
      onBlur,
      onFocus,
      type = "text",
      ...inputProps
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const autocompleteRef = useRef<
      google.maps.places.PlaceAutocompleteElement | null
    >(
      null,
    );

    const [isGoogleReady, setIsGoogleReady] = useState(false);
    const [suggestionRoom, setSuggestionRoom] = useState(0);
    const releaseRoomTimeoutRef = useRef<number | undefined>(undefined);

    useLayoutEffect(() => {
      const element = autocompleteRef.current;

      if (suggestionRoom === 0 || !element) {
        return;
      }

      const releaseRoom = addSuggestionRoom(element, suggestionRoom);
      const shortfall = suggestionShortfall(element);

      if (shortfall > 0) {
        findScrollParent(element).scrollTop += shortfall;
      }

      return releaseRoom;
    }, [suggestionRoom]);

    useEffect(() => () => window.clearTimeout(releaseRoomTimeoutRef.current), []);
    const lastSelectionRef = useRef<string | null>(null);
    const onReadyChangeRef = useRef(onReadyChange);
    onReadyChangeRef.current = onReadyChange;

    useEffect(() => {
      onReadyChangeRef.current?.(isGoogleReady);
    }, [isGoogleReady]);

    const includedPrimaryTypesKey = includedPrimaryTypes?.join(",") ?? "";
    const includeDisplayNameRef = useRef(includeDisplayName);
    includeDisplayNameRef.current = includeDisplayName;
    const preserveSelectionTextRef = useRef(preserveSelectionText);
    preserveSelectionTextRef.current = preserveSelectionText;
    const biasLocationRef = useRef(biasLocation);
    biasLocationRef.current = biasLocation;
    const restrictToBiasLocationRef = useRef(restrictToBiasLocation);
    restrictToBiasLocationRef.current = restrictToBiasLocation;

    const currentValue = getInputValue(
      value === undefined ? defaultValue : value,
    );

    const valueRef = useRef(currentValue);
    valueRef.current = currentValue;

    const handlersRef = useRef({
      name,
      onBlur,
      onChange,
      onFocus,
      onPlaceSelect,
    });

    handlersRef.current = {
      name,
      onBlur,
      onChange,
      onFocus,
      onPlaceSelect,
    };

    const widgetPropsRef = useRef<WidgetProps>({
      className,
      disabled,
      error,
      id,
      label,
      maxLength,
      name,
      placeholder,
      readOnly,
      value: currentValue,
      includedPrimaryTypes,
    });

    widgetPropsRef.current = {
      className,
      disabled,
      error,
      id,
      label,
      maxLength,
      name,
      placeholder,
      readOnly,
      value: currentValue,
      includedPrimaryTypes,
    };

    useEffect(() => {
      const element = autocompleteRef.current;

      if (!element) {
        return;
      }

      applyWidgetProps(element, widgetPropsRef.current);
    }, [
      className,
      currentValue,
      disabled,
      error,
      id,
      label,
      maxLength,
      name,
      placeholder,
      readOnly,
      includedPrimaryTypesKey,
    ]);

    useEffect(() => {
      const container = containerRef.current;
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

      if (!container || !apiKey) {
        return;
      }

      let isActive = true;
      let autocomplete:
        | google.maps.places.PlaceAutocompleteElement
        | undefined;

      const emitChange = (nextValue: string) => {
        handlersRef.current.onChange?.({
          target: {
            name: handlersRef.current.name ?? "",
            value: nextValue,
          },
          currentTarget: {
            name: handlersRef.current.name ?? "",
            value: nextValue,
          },
        } as unknown as ChangeEvent<HTMLInputElement>);
      };

      const initialiseAutocomplete = async () => {
        try {
          configureGoogleMaps(apiKey);

          const { PlaceAutocompleteElement } = await importLibrary("places");
          const destination = biasLocationRef.current?.trim();
          const resolvedLocation = destination
            ? await resolveLocationBias(destination).catch((error: unknown) => {
              console.warn(
                "Could not resolve the destination for place suggestions:",
                error,
              );

              return null;
            })
            : null;

          if (!isActive || !containerRef.current) {
            return;
          }

          const initialTypes = widgetPropsRef.current.includedPrimaryTypes;
          const autocompleteOptions:
            google.maps.places.PlaceAutocompleteElementOptions = {};

          if (initialTypes?.length) {
            autocompleteOptions.includedPrimaryTypes = initialTypes;
          }

          if (resolvedLocation) {
            if (restrictToBiasLocationRef.current) {
              autocompleteOptions.locationRestriction =
                resolvedLocation.viewport;
            } else {
              autocompleteOptions.locationBias = {
                center: resolvedLocation.location,
                radius: 50_000,
              };
            }
          }

          autocomplete = new PlaceAutocompleteElement(autocompleteOptions);

          applyWidgetProps(autocomplete, {
            ...widgetPropsRef.current,
            value: valueRef.current,
          });

          const handleInput = () => {
            if (!autocomplete) {
              return;
            }

            emitChange(autocomplete.value);

            if (autocomplete.value !== lastSelectionRef.current) {
              lastSelectionRef.current = null;
              handlersRef.current.onPlaceSelect?.(null);
            }
          };

          const handleSelect = async (event: Event) => {
            if (!autocomplete) {
              return;
            }

            const { placePrediction } =
              event as google.maps.places.PlacePredictionSelectEvent;

            let selectedValue = autocomplete.value;
            const predictionText = placePrediction.text.toString();

            if (preserveSelectionTextRef.current && predictionText) {
              selectedValue = predictionText;
            } else {
              try {
                const place = placePrediction.toPlace();

                await place.fetchFields({
                  fields: ["displayName", "formattedAddress"],
                });

                selectedValue = formatSelection(
                  place.displayName ?? undefined,
                  place.formattedAddress ?? undefined,
                  includeDisplayNameRef.current,
                ) ?? autocomplete.value;
              } catch {
                selectedValue = autocomplete.value;
              }
            }

            autocomplete.value = selectedValue;
            emitChange(selectedValue);
            setSuggestionRoom(0);
            lastSelectionRef.current = selectedValue;
            handlersRef.current.onPlaceSelect?.({ placeId: placePrediction.placeId, text: selectedValue });
          };

          const handleFocus = (event: Event) => {
            window.clearTimeout(releaseRoomTimeoutRef.current);

            if (autocomplete) {
              const shortfall = suggestionShortfall(autocomplete);

              if (shortfall > 0) {
                setSuggestionRoom((room) => room || shortfall);
              }
            }

            handlersRef.current.onFocus?.(
              event as unknown as FocusEvent<HTMLInputElement>,
            );
          };

          const handleBlur = (event: Event) => {
            const nextTarget = (event as Event & { relatedTarget?: EventTarget | null }).relatedTarget ?? null;

            if (!(nextTarget instanceof Node && autocomplete?.contains(nextTarget))) {
              window.clearTimeout(releaseRoomTimeoutRef.current);
              releaseRoomTimeoutRef.current = window.setTimeout(
                () => setSuggestionRoom(0),
                SUGGESTION_ROOM_RELEASE_DELAY_MS,
              );
            }

            handlersRef.current.onBlur?.(
              event as unknown as FocusEvent<HTMLInputElement>,
            );
          };

          autocomplete.addEventListener("input", handleInput);
          autocomplete.addEventListener(
            "gmp-select",
            handleSelect as EventListener,
          );
          autocomplete.addEventListener("focusin", handleFocus);
          autocomplete.addEventListener("focusout", handleBlur);

          container.replaceChildren(autocomplete);
          autocompleteRef.current = autocomplete;
          setIsGoogleReady(true);

          return () => {
            autocomplete?.removeEventListener(
              "input",
              handleInput,
            );
            autocomplete?.removeEventListener(
              "gmp-select",
              handleSelect as EventListener,
            );
            autocomplete?.removeEventListener(
              "focusin",
              handleFocus,
            );
            autocomplete?.removeEventListener(
              "focusout",
              handleBlur,
            );
          };
        } catch (error: unknown) {
          console.error(
            "Could not initialise Google place autocomplete:",
            error,
          );

          if (isActive) {
            setIsGoogleReady(false);
          }

          return undefined;
        }
      };

      let removeListeners: (() => void) | undefined;

      void initialiseAutocomplete().then((cleanup) => {
        if (!isActive) {
          cleanup?.();
          return;
        }

        removeListeners = cleanup;
      });

      return () => {
        isActive = false;
        removeListeners?.();

        if (autocompleteRef.current === autocomplete) {
          autocompleteRef.current = null;
        }

        container.replaceChildren();
      };
    }, []);

    return (
      <div
        className={cn(
          "form-control w-full max-w-full",
          wrapperClassName,
        )}
      >
        {label
          ? (
            <label className="label" htmlFor={id}>
              <span className="label-text">{label}</span>
            </label>
          )
          : null}

        <div
          ref={containerRef}
          className={cn(
            "w-full max-w-full",
            !isGoogleReady && "hidden",
          )}
        />

        {!isGoogleReady
          ? (
            <input
              ref={ref}
              id={id}
              name={name}
              type={type}
              className={cn(
                "input w-full max-w-full outline-none",
                {
                  "input-ghost": color === "ghost",
                  "input-primary": color === "primary",
                  "input-secondary": color === "secondary",
                  "input-accent": color === "accent",
                  "input-info": color === "info",
                  "input-success": color === "success",
                  "input-warning": color === "warning",
                  "input-error": Boolean(error) || color === "error",
                  "input-xs": size === "xs",
                  "input-sm": size === "sm",
                  "input-md": size === "md",
                  "input-lg": size === "lg",
                  "input-bordered": bordered,
                },
                className,
              )}
              value={value}
              defaultValue={value === undefined ? defaultValue : undefined}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              maxLength={maxLength}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              aria-invalid={Boolean(error)}
              aria-describedby={error && id ? `${id}-error` : undefined}
              {...inputProps}
            />
          )
          : null}

        {error
          ? (
            <label className="label">
              <span
                id={id ? `${id}-error` : undefined}
                className="label-text-alt text-error"
              >
                {error}
              </span>
            </label>
          )
          : null}
      </div>
    );
  },
);

AutocompleteInput.displayName = "AutocompleteInput";

export default AutocompleteInput;
